import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Award, TrendingUp, Sparkles, ArrowRight, Home, Heart, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useGamificationStore } from '../../stores/gamificationStore';
import SharedAssessmentModal from './SharedAssessmentModal';
import PartnerComparisonResults from './PartnerComparisonResults';
import * as sharingService from '../../services/assessmentSharingService';
import { savePlace, loadPlace, clearPlace } from '../../services/sharedSessionPlace';

/**
 * Generic Relationship Assessment Viewer
 *
 * Handles all assessment types with support for:
 * - Scale questions (1-10 sliders)
 * - Scenario questions (multiple choice)
 * - Ranking questions (drag and drop)
 * - Partner sharing for Values Assessment (comparePartnerScores enabled)
 */
const RelationshipAssessmentViewer = ({ assessment, assessmentKey, initialJoinCode }) => {
  const navigate = useNavigate();
  const { assessmentResults, setAssessmentResults } = useApp();

  const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'questions', 'results'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [scaleValue, setScaleValue] = useState(5);
  const [ranking, setRanking] = useState([]);

  // Partner sharing state — a join link opens the join dialog immediately
  // with the shared session code pre-filled
  const supportsSharing = assessment.comparePartnerScores !== undefined;
  const openedViaJoinLink = supportsSharing && Boolean(initialJoinCode);
  // A partner session this device was already in (services/sharedSessionPlace.js).
  // Reopening the same session's join link resumes it rather than joining again.
  const [storedPlace] = useState(() => (supportsSharing ? loadPlace(assessment.id) : null));
  const resumingJoinLink = openedViaJoinLink && !!storedPlace
    && storedPlace.sessionCode === String(initialJoinCode).toUpperCase();
  const [showSharingModal, setShowSharingModal] = useState(openedViaJoinLink && !resumingJoinLink);
  const [sharingMode, setSharingMode] = useState(openedViaJoinLink ? 'join' : null); // 'start' or 'join'
  const [sharedSession, setSharedSession] = useState(null); // { sessionCode, partnerNumber, partnerName, sessionData }
  const [resumed, setResumed] = useState(false);

  // Pick up a partner session after a reload: same partner, same answers,
  // same question. Runs once, for the place found when the page opened.
  React.useEffect(() => {
    if (!storedPlace || (openedViaJoinLink && !resumingJoinLink)) return undefined;
    let cancelled = false;
    sharingService.getSharedAssessment(storedPlace.sessionCode).then((result) => {
      if (cancelled) return;
      const me = result.success
        ? (storedPlace.partnerNumber === 1 ? result.data.partnerOne : result.data.partnerTwo)
        : null;
      // Gone (expired or never there), or someone else holds this place.
      if (!me || (storedPlace.partnerNumber === 2 && me.name !== storedPlace.partnerName)) {
        clearPlace(assessment.id);
        return;
      }
      setSharedSession({
        sessionCode: storedPlace.sessionCode,
        partnerNumber: storedPlace.partnerNumber,
        partnerName: storedPlace.partnerName,
        sessionData: null
      });
      setShowSharingModal(false);
      if (me.status === 'completed' || storedPlace.step === 'results') {
        setCurrentStep('results');
      } else if (storedPlace.step === 'questions') {
        setResponses(storedPlace.responses || []);
        setCurrentQuestionIndex(Math.min(storedPlace.questionIndex || 0, assessment.questionCount - 1));
        setCurrentStep('questions');
      }
      setResumed(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember this device's place in a partner session.
  const rememberPlace = (session, step, questionIndex, answers) => {
    if (!session) return;
    savePlace(assessment.id, {
      sessionCode: session.sessionCode,
      partnerNumber: session.partnerNumber,
      partnerName: session.partnerName,
      step,
      questionIndex,
      responses: answers
    });
  };

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / assessment.questionCount) * 100;

  // Initialize ranking for ranking questions
  React.useEffect(() => {
    if (currentQuestion && currentQuestion.type === 'ranking' && currentQuestion.items) {
      setRanking(currentQuestion.items.map(item => item.id));
    }
  }, [currentQuestionIndex, currentQuestion]);

  const handleStartAssessment = () => {
    rememberPlace(sharedSession, 'questions', 0, []);
    setCurrentStep('questions');
    setCurrentQuestionIndex(0);
    setResponses([]);
    setSelectedOption(null);
    setScaleValue(5);
  };

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleScaleChange = (value) => {
    setScaleValue(value);
  };

  const handleRankingChange = (newRanking) => {
    setRanking(newRanking);
  };

  // Partner sharing handlers
  const handleStartShared = async (partnerName) => {
    const result = await sharingService.createSharedAssessment(assessment.id, partnerName);
    if (result.success) {
      const session = {
        sessionCode: result.sessionCode,
        partnerNumber: 1,
        partnerName,
        sessionData: null
      };
      setSharedSession(session);
      rememberPlace(session, 'intro', 0, []);
    }
    return result;
  };

  const handleJoinShared = async (sessionCode, partnerName) => {
    const result = await sharingService.joinSharedAssessment(sessionCode, partnerName);
    if (result.success) {
      const session = {
        sessionCode: sessionCode.toUpperCase(),
        partnerNumber: 2,
        partnerName,
        sessionData: null
      };
      setSharedSession(session);
      // Start assessment automatically after joining
      handleStartAssessment();
      rememberPlace(session, 'questions', 0, []);
    }
    return result;
  };

  const handleNextQuestion = async () => {
    // Validate response based on question type
    if (currentQuestion.type === 'scenario' && selectedOption === null) return;
    if (currentQuestion.type === 'scale' && scaleValue === null) return;
    if (currentQuestion.type === 'ranking' && ranking.length === 0) return;

    // Save response
    const newResponse = {
      questionId: currentQuestion.id,
      questionText: currentQuestion.text
    };

    if (currentQuestion.type === 'scenario') {
      newResponse.selectedIndex = selectedOption;
      newResponse.answerText = currentQuestion.options[selectedOption].text;
    } else if (currentQuestion.type === 'scale') {
      newResponse.value = scaleValue;
    } else if (currentQuestion.type === 'ranking') {
      newResponse.ranking = ranking;
    }

    const newResponses = [...responses, newResponse];
    setResponses(newResponses);

    // Reset for next question
    setSelectedOption(null);
    setScaleValue(5);

    // Check if this was the last question
    if (currentQuestionIndex === assessment.questionCount - 1) {
      // Calculate final scores and generate results
      const scores = assessment.calculateScores
        ? assessment.calculateScores(newResponses)
        : calculateDefaultScores(newResponses);

      const resultType = assessment.getHealthLevel
        ? assessment.getHealthLevel(calculateAverage(scores))
        : assessment.getProfileType
        ? assessment.getProfileType(scores)
        : assessment.getPatternType
        ? assessment.getPatternType(scores)
        : assessment.getAlignmentType
        ? assessment.getAlignmentType(scores)
        : 'default';

      const resultData = assessment.healthLevels
        ? assessment.healthLevels[resultType]
        : assessment.profileTypes
        ? assessment.profileTypes[resultType]
        : assessment.patternTypes
        ? assessment.patternTypes[resultType]
        : assessment.alignmentTypes
        ? assessment.alignmentTypes[resultType]
        : null;

      // Build dimensions/areas/categories with scores
      const dimensionsWithScores = {};
      const dimensionSource = assessment.dimensions || assessment.areas || assessment.patternCategories || assessment.valueCategories;

      if (dimensionSource) {
        Object.entries(dimensionSource).forEach(([key, dimension]) => {
          dimensionsWithScores[key] = {
            ...dimension,
            score: scores[key] || calculateDimensionScore(key, newResponses, assessment)
          };
        });
      }

      const avgScore = calculateAverage(scores);

      const results = {
        assessmentId: assessment.id,
        assessmentKey: assessmentKey,
        resultType,
        resultData,
        dimensions: dimensionsWithScores,
        scores,
        avgScore,
        responses: newResponses,
        dateTaken: new Date().toISOString()
      };

      // Save to context (using assessmentKey to store multiple assessments)
      const updatedResults = {
        ...(assessmentResults || {}),
        [assessmentKey]: results
      };
      setAssessmentResults(updatedResults);

      // Award gamification points
      const gamification = useGamificationStore.getState();
      if (!assessmentResults || !assessmentResults[assessmentKey]) {
        gamification.completeAssessment();
      } else {
        gamification.retakeAssessment();
      }

      // Save to Firebase if in shared mode
      if (sharedSession) {
        await sharingService.updatePartnerProgress(
          sharedSession.sessionCode,
          sharedSession.partnerNumber,
          newResponses,
          scores,
          'completed'
        );
        rememberPlace(sharedSession, 'results', currentQuestionIndex, newResponses);
      }

      setCurrentStep('results');
    } else {
      // Move to next question
      rememberPlace(sharedSession, 'questions', currentQuestionIndex + 1, newResponses);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      const previousResponse = responses[currentQuestionIndex - 1];
      if (previousResponse) {
        if (previousResponse.selectedIndex !== undefined) {
          setSelectedOption(previousResponse.selectedIndex);
        } else if (previousResponse.value !== undefined) {
          setScaleValue(previousResponse.value);
        } else if (previousResponse.ranking) {
          setRanking(previousResponse.ranking);
        }
        setResponses(responses.slice(0, -1));
        rememberPlace(sharedSession, 'questions', currentQuestionIndex - 1, responses.slice(0, -1));
      }
    }
  };

  const handleRetake = () => {
    setCurrentStep('intro');
    setCurrentQuestionIndex(0);
    setResponses([]);
    setSelectedOption(null);
    setScaleValue(5);
  };

  // Helper functions
  const calculateDefaultScores = (responses) => {
    // Default scoring if assessment doesn't provide custom logic
    const scores = {};
    responses.forEach(response => {
      if (response.value !== undefined) {
        const question = assessment.questions.find(q => q.id === response.questionId);
        if (question && question.dimension) {
          scores[question.dimension] = (scores[question.dimension] || 0) + response.value;
        }
      }
    });
    return scores;
  };

  const calculateDimensionScore = (dimensionKey, responses, assessment) => {
    // Calculate score for a specific dimension from questions
    const dimensionQuestions = assessment.questions.filter(q =>
      q.dimension === dimensionKey ||
      q.category === dimensionKey ||
      q.area === dimensionKey
    );

    const dimensionResponses = responses.filter(r =>
      dimensionQuestions.some(q => q.id === r.questionId)
    );

    if (dimensionResponses.length === 0) return 50;

    const total = dimensionResponses.reduce((sum, r) => sum + (r.value || 5), 0);
    const avg = total / dimensionResponses.length;
    return Math.round((avg / 10) * 100); // Convert 1-10 scale to 0-100
  };

  const calculateAverage = (scores) => {
    const values = Object.values(scores).filter(v => typeof v === 'number');
    if (values.length === 0) return 50;
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  };

  // Intro Screen
  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <div className="w-32 h-32 md:w-44 md:h-44 mx-auto bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center">
                <Heart className="w-16 h-16 md:w-20 md:h-20 text-white" />
              </div>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {assessment.title}
            </h1>
            <p className="text-xl text-slate-300 mb-2">
              {assessment.subtitle}
            </p>
            <div className="flex items-center justify-center gap-4 text-slate-400 text-sm">
              <span>{assessment.duration}</span>
              <span>•</span>
              <span>{assessment.questionCount} questions</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                {assessment.introduction.heading}
              </h2>
              <div className="prose prose-invert max-w-none mb-6">
                {assessment.introduction.content.split('\n\n').map((paragraph, idx) => {
                  // Handle bold text
                  const parts = paragraph.split(/\*\*(.*?)\*\*/g);
                  return (
                    <p key={idx} className="text-slate-300 mb-4 last:mb-0">
                      {parts.map((part, i) =>
                        i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{part}</strong> : part
                      )}
                    </p>
                  );
                })}
              </div>

              <div className="flex gap-4 justify-center mt-8">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <Button onClick={handleStartAssessment} className="px-8">
                  Begin Assessment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Already in a partner session (created here, or picked up after a reload) */}
              {supportsSharing && sharedSession && (
                <div className="mt-6 pt-6 border-t border-slate-700 text-center">
                  <p className="text-slate-300 text-sm">
                    You&apos;re in a shared session. Your partner joins with code{' '}
                    <span className="font-mono font-semibold text-rose-300">{sharedSession.sessionCode}</span>.
                    Begin whenever you&apos;re ready.
                  </p>
                </div>
              )}

              {/* Partner Sharing Options (Values Assessment only) */}
              {supportsSharing && !sharedSession && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <p className="text-center text-slate-400 text-sm mb-4">
                    Or take this assessment together with your partner
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSharingMode('start');
                        setShowSharingModal(true);
                      }}
                      className="flex-1 max-w-xs"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Take with Partner
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSharingMode('join');
                        setShowSharingModal(true);
                      }}
                      className="flex-1 max-w-xs"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Join Partner
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        </div>

        {/* Sharing Modal */}
        <SharedAssessmentModal
          isOpen={showSharingModal}
          onClose={() => setShowSharingModal(false)}
          mode={sharingMode}
          onStart={handleStartShared}
          onJoin={handleJoinShared}
          sessionCode={initialJoinCode || undefined}
        />
      </div>
    );
  }

  // Questions Screen
  if (currentStep === 'questions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8"
          >
            <h1 className="text-lg font-semibold text-white mb-2">{assessment.title}</h1>
            {sharedSession && (
              <p className="text-sm text-slate-400 mb-2" data-testid="shared-session-note">
                Shared session, code{' '}
                <span className="font-mono text-rose-300">{sharedSession.sessionCode}</span>
                {resumed ? '. Picked up where you left off.' : '.'}
              </p>
            )}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">
                Question {currentQuestionIndex + 1} of {assessment.questionCount}
              </span>
              <span className="text-sm text-rose-400 font-semibold">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <motion.div
                className="h-2 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 mb-6">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {currentQuestion.text}
                </h2>

                {/* Scale Question */}
                {currentQuestion.type === 'scale' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                      <span>{currentQuestion.lowLabel}</span>
                      <span>{currentQuestion.highLabel}</span>
                    </div>
                    <input
                      aria-label={`${currentQuestion.lowLabel} to ${currentQuestion.highLabel}, 1 to 10`}
                      type="range"
                      min="1"
                      max="10"
                      value={scaleValue}
                      onChange={(e) => handleScaleChange(parseInt(e.target.value))}
                      className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer slider-rose"
                    />
                    <div className="text-center">
                      <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-500">
                        {scaleValue}
                      </span>
                      <span className="text-slate-400 text-lg ml-2">/ 10</span>
                    </div>
                    {currentQuestion.reflectionPrompt && (
                      <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <p className="text-sm text-slate-300 italic">
                          💭 Reflect: {currentQuestion.reflectionPrompt}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Scenario Question */}
                {currentQuestion.type === 'scenario' && (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => (
                      <motion.button
                        key={idx}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => handleOptionSelect(idx)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          selectedOption === idx
                            ? 'border-rose-500 bg-rose-500/10'
                            : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                            selectedOption === idx
                              ? 'border-rose-500 bg-rose-500'
                              : 'border-slate-600'
                          }`}>
                            {selectedOption === idx && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <span className="text-slate-200">{option.text}</span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Ranking Question */}
                {currentQuestion.type === 'ranking' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-400 mb-4">
                      Drag to reorder, or use the numbers to rank from most important (1) to least important ({currentQuestion.items.length})
                    </p>
                    {currentQuestion.items.map((item) => {
                      const currentRank = ranking.indexOf(item.id) + 1;
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                        >
                          <select
                            aria-label={`Rank for ${item.text}`}
                            value={currentRank}
                            onChange={(e) => {
                              const newRank = parseInt(e.target.value) - 1;
                              const newRanking = [...ranking];
                              const currentIndex = ranking.indexOf(item.id);
                              newRanking.splice(currentIndex, 1);
                              newRanking.splice(newRank, 0, item.id);
                              handleRankingChange(newRanking);
                            }}
                            className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white"
                          >
                            {currentQuestion.items.map((_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                          <span className="text-slate-200 flex-1">{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="secondary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={handleNextQuestion}
                  disabled={
                    (currentQuestion.type === 'scenario' && selectedOption === null) ||
                    (currentQuestion.type === 'scale' && scaleValue === null) ||
                    (currentQuestion.type === 'ranking' && ranking.length === 0)
                  }
                  className="px-8"
                >
                  {currentQuestionIndex === assessment.questionCount - 1 ? 'See Results' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Results Screen
  if (currentStep === 'results') {
    // If in shared mode, show partner comparison instead
    if (sharedSession) {
      return (
        <PartnerComparisonResults
          assessment={assessment}
          sessionCode={sharedSession.sessionCode}
          partnerNumber={sharedSession.partnerNumber}
          partnerName={sharedSession.partnerName}
        />
      );
    }

    // Regular individual results
    const results = assessmentResults?.[assessmentKey];
    if (!results) return null;

    const { resultData, dimensions } = results;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {assessment.completionMessage?.heading || 'Your Results'}
            </h1>
            <p className="text-xl text-slate-300">
              {assessment.completionMessage?.content?.split('\n\n')[0] || 'Here are your assessment results'}
            </p>
          </motion.div>

          {/* Result Type Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-8 bg-gradient-to-br from-rose-500/10 to-pink-500/10 border-rose-500/20">
              <div className="text-center mb-6">
                {resultData.icon && (
                  <div className="text-6xl mb-4">{resultData.icon}</div>
                )}
                <h2 className="text-3xl font-bold text-white mb-2">
                  {resultData.name}
                </h2>
                <p className="text-xl text-rose-200 mb-4">
                  {resultData.tagline}
                </p>
                <p className="text-slate-300 max-w-2xl mx-auto">
                  {resultData.description}
                </p>
              </div>

              {(resultData.strengths || resultData.challenges || resultData.growthOpportunities || resultData.growthAreas || resultData.focus || resultData.considerations) && (
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {/* Strengths */}
                  {resultData.strengths && (
                    <div>
                      <h3 className="text-sm font-semibold text-rose-300 uppercase mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Strengths
                      </h3>
                      <ul className="space-y-2">
                        {resultData.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <span className="text-green-400 mt-1">✓</span>
                            <span>{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Growth Areas / Challenges / Focus / Considerations */}
                  {(resultData.growthAreas || resultData.challenges || resultData.growthOpportunities || resultData.focus || resultData.considerations) && (
                    <div>
                      <h3 className="text-sm font-semibold text-amber-300 uppercase mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        {resultData.growthAreas ? 'Growth Areas' : resultData.challenges ? 'Challenges' : resultData.growthOpportunities ? 'Growth Opportunities' : resultData.focus ? 'Focus Areas' : 'Considerations'}
                      </h3>
                      <ul className="space-y-2">
                        {(resultData.growthAreas || resultData.challenges || resultData.growthOpportunities || resultData.focus || resultData.considerations).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <span className="text-amber-400 mt-1">→</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Dimension Scores */}
          {dimensions && Object.keys(dimensions).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Your Scores Across All Dimensions
                </h3>
                <div className="space-y-4">
                  {Object.entries(dimensions).map(([key, dimension]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {dimension.icon && <span className="text-2xl">{dimension.icon}</span>}
                          <span className="text-white font-medium">{dimension.name}</span>
                        </div>
                        <span className="text-rose-400 font-bold">{dimension.score}/100</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3">
                        <motion.div
                          className="h-3 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${dimension.score}%` }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        />
                      </div>
                      {dimension.description && (
                        <p className="text-sm text-slate-400">{dimension.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}

          {/* Next Steps */}
          {resultData.nextSteps && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <Card className="p-8 bg-gradient-to-br from-teal-500/5 to-blue-500/5 border-teal-500/20">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Your Recommended Path
                </h3>
                <ul className="space-y-3 mb-6">
                  {resultData.nextSteps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <span className="text-slate-300">{step}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-4 justify-center pt-6 border-t border-teal-500/20">
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/assessment')}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    All Assessments
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleRetake}
                  >
                    Retake Assessment
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/modules')}
                  >
                    Explore Modules
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default RelationshipAssessmentViewer;
